// src/modules/competitions/components/CompactVideoUploader.tsx

import React, { useRef, useState, useEffect } from 'react';
import Button from '../../../shared/components/ui/Button';
import { CompetitionVideoType } from '../types';
import { videoTypesService } from '../services/videoTypesService';
import '../styles/compactUploader.css';

interface Props {
    videoType: CompetitionVideoType;
    onFileSelect: (file: File, url: string, duration: number) => void;
    onUpload: () => void;
    onCancel: () => void;
    isUploading: boolean;
    selectedFile: File | null;
    previewUrl: string | null;
    error: string | null;
}

const CompactVideoUploader: React.FC<Props> = ({
    videoType,
    onFileSelect,
    onUpload,
    onCancel,
    isUploading,
    selectedFile,
    previewUrl,
    error
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [localError, setLocalError] = useState<string | null>(null);
    const [videoDuration, setVideoDuration] = useState<number>(0);
    const [validating, setValidating] = useState(false);

    useEffect(() => {
        setLocalError(error);
    }, [error]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setLocalError(null);
        setValidating(true);

        // Validar formato y tamaño
        const validation = videoTypesService.validateVideoFile(file, videoType);
        if (!validation.isValid) {
            setLocalError(validation.error || 'Archivo no válido');
            setValidating(false);
            return;
        }

        // Validar duración
        const durationValidation = await videoTypesService.validateVideoDuration(
            file,
            videoType.max_duration_seconds
        );

        if (!durationValidation.isValid) {
            setLocalError(durationValidation.error || 'Duración no válida');
            setValidating(false);
            return;
        }

        // Crear preview URL
        const url = URL.createObjectURL(file);
        setVideoDuration(durationValidation.duration || 0);
        onFileSelect(file, url, durationValidation.duration || 0);
        setValidating(false);
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleCancelClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setLocalError(null);
        setVideoDuration(0);
        onCancel();
    };

    return (
        <div className="compact-uploader">
            <input
                ref={fileInputRef}
                type="file"
                accept={videoType.allowed_formats.map(f => `.${f}`).join(',')}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={isUploading || validating}
            />

            {!selectedFile ? (
                <div className="upload-prompt" onClick={handleClick}>
                    <div className="upload-icon">🎥</div>
                    <p className="upload-text">
                        <span className="upload-link">Haz clic aquí</span> para seleccionar tu video
                    </p>
                    <p className="upload-hint">
                        Formatos: {videoType.allowed_formats.map(f => f.toUpperCase()).join(', ')} | 
                        Máx: {videoType.max_file_size_mb}MB | 
                        Duración máx: {videoType.max_duration_seconds}s
                    </p>
                </div>
            ) : (
                <div className="upload-preview">
                    <div className="preview-video-container">
                        <video
                            src={previewUrl || ''}
                            controls
                            className="preview-video"
                        />
                    </div>
                    
                    <div className="file-details">
                        <p className="file-name">📹 {selectedFile.name}</p>
                        <div className="file-meta">
                            <span className="file-size">
                                {videoTypesService.formatFileSize(selectedFile.size)}
                            </span>
                            {videoDuration > 0 && (
                                <>
                                    <span className="meta-separator">•</span>
                                    <span className="file-duration">
                                        ⏱️ {videoTypesService.formatDuration(videoDuration)}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="upload-actions">
                        <Button
                            onClick={handleCancelClick}
                            variant="secondary"
                            disabled={isUploading}
                            className="btn-cancel"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={onUpload}
                            variant="primary"
                            disabled={isUploading}
                            className="btn-upload"
                        >
                            {isUploading ? '⏳ Subiendo...' : '📤 Subir Video'}
                        </Button>
                    </div>
                </div>
            )}

            {(localError || validating) && (
                <div className={`upload-message ${localError ? 'error' : 'info'}`}>
                    {validating ? '⏳ Validando video...' : `⚠️ ${localError}`}
                </div>
            )}
        </div>
    );
};

export default CompactVideoUploader;